import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

export default function PublicSite() {
    const { slug } = useParams();
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        api
            .publicMeta(slug)
            .then((d) => !cancelled && setMeta(d))
            .catch(() => !cancelled && setError("Site not found or unpublished."));
        return () => {
            cancelled = true;
        };
    }, [slug]);

    useEffect(() => {
        if (meta?.name) document.title = `${meta.name} · Loom`;
    }, [meta]);

    // Dynamically inject OG / Twitter card meta tags so link unfurls
    // (WhatsApp, Twitter, iMessage, LinkedIn, Slack, Discord) show the
    // auto-generated social share image and site title/description.
    useEffect(() => {
        if (!meta) return;
        const title = meta.title || meta.name || "Loom site";
        const description = meta.description || "Built with Loom — the AI website builder.";
        const image = meta.og_image_url || "";
        const canonical = meta.canonical_url || window.location.href;

        const tags = [
            { attr: "property", key: "og:type", content: "website" },
            { attr: "property", key: "og:site_name", content: "Loom" },
            { attr: "property", key: "og:title", content: title },
            { attr: "property", key: "og:description", content: description },
            { attr: "property", key: "og:url", content: canonical },
            { attr: "name", key: "twitter:title", content: title },
            { attr: "name", key: "twitter:description", content: description },
            { attr: "name", key: "twitter:card", content: image ? "summary_large_image" : "summary" },
            { attr: "name", key: "description", content: description },
        ];
        if (image) {
            tags.push(
                { attr: "property", key: "og:image", content: image },
                { attr: "property", key: "og:image:width", content: "1200" },
                { attr: "property", key: "og:image:height", content: "630" },
                { attr: "property", key: "og:image:alt", content: `${title} — social share card` },
                { attr: "name", key: "twitter:image", content: image },
            );
        }

        const injected = tags.map(({ attr, key, content }) => {
            let el = document.head.querySelector(`meta[${attr}="${key}"]`);
            const created = !el;
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            const prev = el.getAttribute("content");
            el.setAttribute("content", content);
            el.setAttribute("data-loom-og", "1");
            return { el, created, prev };
        });

        // Canonical link
        let canonicalEl = document.head.querySelector('link[rel="canonical"]');
        const canonicalCreated = !canonicalEl;
        const canonicalPrev = canonicalEl ? canonicalEl.getAttribute("href") : null;
        if (!canonicalEl) {
            canonicalEl = document.createElement("link");
            canonicalEl.setAttribute("rel", "canonical");
            canonicalEl.setAttribute("data-loom-og", "1");
            document.head.appendChild(canonicalEl);
        }
        canonicalEl.setAttribute("href", canonical);

        return () => {
            injected.forEach(({ el, created, prev }) => {
                if (created) el.remove();
                else if (prev !== null) el.setAttribute("content", prev);
            });
            if (canonicalCreated) canonicalEl.remove();
            else if (canonicalPrev !== null) canonicalEl.setAttribute("href", canonicalPrev);
        };
    }, [meta]);

    if (error) {
        return (
            <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
                <div className="max-w-md text-center p-10 border border-border">
                    <div className="overline mb-4">▍ 404 · Nothing here</div>
                    <h1 className="text-3xl font-black tracking-tight">This canvas is unpublished.</h1>
                    <p className="mt-3 text-sm text-muted-foreground">
                        The link may be wrong, or the owner has taken it down.
                    </p>
                    <Link
                        to="/"
                        className="mt-6 inline-flex items-center gap-2 btn-outline-brutal !h-9 !text-[10px]"
                    >
                        Back to Loom <ArrowUpRight className="w-3 h-3" strokeWidth={1.5} />
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <iframe
                title={meta?.name || "site"}
                src={api.publicSiteUrl(slug)}
                sandbox="allow-scripts allow-forms allow-popups allow-same-origin"
                className="w-full h-screen border-0 bg-white"
                data-testid="public-site-iframe"
            />
        </div>
    );
}
